import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ShieldAlert, RefreshCw, ExternalLink, Search, AlertTriangle, CheckCircle2,
  XCircle, UserRound, Flag, StickyNote, Clock, Sparkles, Share2, Shield,
  FileText, Download, Send, Mail, Pencil, Loader2, Trash2,
} from "lucide-react";
import { toast } from "sonner";

/* ---------- UK date formatters (spec: DD/MM/YYYY) ------------------- */
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-GB", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return iso; }
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-GB", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return iso; }
};

const STATUSES = ["New", "Under review", "Dismissed", "Actioned", "Archived"];
const PRIORITIES = ["Unset", "Informational", "Low", "Medium", "High", "Critical"];
const SOURCES = [
  { value: "kev", label: "CISA KEV" },
  { value: "ncsc", label: "UK NCSC" },
];

const STATUS_META = {
  "New":          { cls: "bg-blue-50  text-blue-700  border-blue-200" },
  "Under review": { cls: "bg-amber-50 text-amber-700 border-amber-200" },
  "Dismissed":    { cls: "bg-slate-50 text-slate-600 border-slate-200" },
  "Actioned":     { cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  "Archived":     { cls: "bg-slate-50 text-slate-500 border-slate-200" },
};
const PRIORITY_META = {
  "Critical":      { cls: "bg-rose-100   text-rose-800   border-rose-200" },
  "High":          { cls: "bg-orange-100 text-orange-800 border-orange-200" },
  "Medium":        { cls: "bg-amber-100  text-amber-800  border-amber-200" },
  "Low":           { cls: "bg-slate-100  text-slate-700  border-slate-200" },
  "Informational": { cls: "bg-slate-50   text-slate-500  border-slate-200" },
  "Unset":         { cls: "bg-slate-50   text-slate-500  border-slate-200" },
};

export default function ThreatAdvisories() {
  const [sources, setSources] = useState([]);
  const [liveAt, setLiveAt] = useState(null);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [knownVendors, setKnownVendors] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdfDiag, setPdfDiag] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [openId, setOpenId] = useState(null);

  const [filters, setFilters] = useState({
    status: "", priority: "", source: "", vendor: "",
    q: "", date_from: "", date_to: "",
  });

  const loadQueue = async () => {
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      params.limit = 500;
      const r = await api.get("/threat-advisories/queue", { params });
      setItems(r.data.items || []);
      setCounts(r.data.counts_by_status || {});
      setKnownVendors(r.data.known_vendors || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load queue");
    }
  };

  const loadSources = async () => {
    try {
      const r = await api.get("/threat-advisories/sources");
      setSources(r.data.sources || []);
      setLiveAt(r.data.live_at);
    } catch { /* keep whatever we had */ }
  };

  const loadUsers = async () => {
    try {
      const r = await api.get("/threat-advisories/users");
      setUsers(r.data || []);
    } catch { /* non-fatal */ }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadQueue(), loadSources(), loadUsers()]);
      setLoading(false);
    })();
    (async () => {
      try {
        const r = await api.get("/threat-advisories/diag/pdf-support");
        setPdfDiag(r.data || null);
      } catch { /* non-fatal */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-run the queue query when filters change (debounced lightly on text).
  useEffect(() => {
    const t = setTimeout(loadQueue, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.priority, filters.source, filters.vendor,
      filters.q, filters.date_from, filters.date_to]);

  const refreshNow = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/threat-advisories/refresh");
      const kev = r.data?.kev || {};
      const ncsc = r.data?.ncsc || {};
      const parts = [];
      if (kev.ok) parts.push(`CISA KEV: ${kev.new} new`);
      else parts.push(`CISA KEV: failed`);
      if (ncsc.ok) parts.push(`UK NCSC: ${ncsc.new} new`);
      else parts.push(`UK NCSC: failed`);
      toast.success(`Refreshed — ${parts.join(" · ")}`);
      await Promise.all([loadQueue(), loadSources()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally { setRefreshing(false); }
  };

  const resetFilters = () => setFilters({
    status: "", priority: "", source: "", vendor: "",
    q: "", date_from: "", date_to: "",
  });

  const newCount = counts["New"] || 0;
  const anyStale = sources.some((s) => s.stale_over_24h);
  const anyFailing = sources.some((s) => s.last_poll_success === false);

  return (
    <div className="space-y-6 p-6" data-testid="threat-advisories-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-2">
            <ShieldAlert className="h-7 w-7 text-rose-600" />
            Threat Advisories
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Review queue for CISA KEV and UK NCSC alerts. Advisory authoring and send-out
            are coming later — this page is for triage only.
          </p>
          {liveAt && (
            <p className="text-xs text-slate-400 mt-1" data-testid="threat-live-at">
              Module live since {fmtDateTime(liveAt)} · older KEV entries archived at
              first poll.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {pdfDiag && (
            <div
              className={`text-[10px] font-mono px-2 py-1 rounded border ${
                pdfDiag.soffice_installed
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-amber-50 text-amber-700 border-amber-200"
              }`}
              title={`PDF pipeline:\n${(pdfDiag.pdf_pipeline || []).join("\n")}${pdfDiag.last_pipeline_used ? `\n\nLast download via: ${pdfDiag.last_pipeline_used} (${pdfDiag.last_pipeline_at || "—"})` : ""}`}
              data-testid="threat-pdf-diag"
            >
              PDF ·
              {pdfDiag.soffice_installed
                ? " template-faithful (LibreOffice)"
                : " native fallback"}
              {pdfDiag.last_pipeline_used && (
                <span className="ml-1 text-slate-500">
                  · last: {pdfDiag.last_pipeline_used}
                </span>
              )}
            </div>
          )}
          <Button
            onClick={refreshNow}
            disabled={refreshing}
            data-testid="threat-refresh-btn"
            className="bg-rose-600 hover:bg-rose-700 text-white"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Polling…" : "Refresh now"}
          </Button>
        </div>
      </div>

      {/* Source health strip */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="threat-source-health">
        {sources.map((s) => (
          <Card key={s.source} className={`p-4 border ${
            s.stale_over_24h ? "border-rose-300 bg-rose-50/40"
              : s.last_poll_success === false ? "border-amber-300 bg-amber-50/40"
              : "border-slate-200"
          }`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-800">{s.label}</div>
                <div className="text-xs text-slate-500 mt-0.5 font-mono">
                  {s.source === "kev"
                    ? "cisa.gov · known_exploited_vulnerabilities.json"
                    : "ncsc.gov.uk · all-rss-feed.xml"}
                </div>
                <div className="text-xs text-slate-600 mt-2">
                  Last poll: <span className="font-mono">{fmtDateTime(s.last_poll_at)}</span>
                  {s.last_poll_success === false && (
                    <span className="ml-2 text-amber-700">· <b>failed</b>{s.last_poll_error ? ` — ${s.last_poll_error}` : ""}</span>
                  )}
                </div>
                <div className="text-xs text-slate-500">
                  Last success: <span className="font-mono">{fmtDateTime(s.last_success_at)}</span>
                </div>
              </div>
              {s.stale_over_24h ? (
                <Badge className="bg-rose-100 text-rose-800 border-rose-200">
                  <AlertTriangle className="h-3 w-3 mr-1" /> Stale &gt; 24h
                </Badge>
              ) : s.last_poll_success === false ? (
                <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                  <AlertTriangle className="h-3 w-3 mr-1" /> Last poll failed
                </Badge>
              ) : (
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Healthy
                </Badge>
              )}
            </div>
          </Card>
        ))}
      </div>

      {(anyStale || anyFailing) && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 flex items-center gap-2" data-testid="threat-health-warning">
          <AlertTriangle className="h-4 w-4" />
          One or more sources haven't successfully polled in over 24 hours. Check the
          source URL or re-run <b>Refresh now</b>.
        </div>
      )}

      {/* Status counts strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="threat-status-counts">
        {STATUSES.map((s) => {
          const active = filters.status === s;
          return (
            <button
              key={s}
              onClick={() => setFilters((f) => ({ ...f, status: active ? "" : s }))}
              className={`rounded-md border p-3 text-left transition-all ${STATUS_META[s].cls} ${active ? "ring-2 ring-offset-1 ring-blue-400" : ""}`}
              data-testid={`threat-count-${s.replace(/\s/g,'-').toLowerCase()}`}
            >
              <div className="text-2xl font-bold tabular-nums">{counts[s] ?? 0}</div>
              <div className="text-[11px] font-medium">{s}{s === "New" && newCount > 0 ? " · action needed" : ""}</div>
            </button>
          );
        })}
      </div>

      {/* Filter bar */}
      <Card className="p-4" data-testid="threat-filter-bar">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="h-4 w-4 absolute left-2 top-2.5 text-slate-400 pointer-events-none" />
            <Input
              placeholder="Search title, summary, CVE, vendor…"
              className="pl-8"
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              data-testid="threat-search"
            />
          </div>
          <Select value={filters.source || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, source: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="threat-filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              {SOURCES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.priority || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, priority: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="threat-filter-priority"><SelectValue placeholder="Severity" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All severities</SelectItem>
              {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.vendor || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, vendor: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="threat-filter-vendor"><SelectValue placeholder="Vendor" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All vendors</SelectItem>
              {knownVendors.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex gap-2 md:col-span-6">
            <div className="flex-1">
              <label className="text-[11px] text-slate-500">Published from</label>
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} data-testid="threat-date-from" />
            </div>
            <div className="flex-1">
              <label className="text-[11px] text-slate-500">Published to</label>
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} data-testid="threat-date-to" />
            </div>
            <div className="self-end">
              <Button variant="outline" onClick={resetFilters} data-testid="threat-reset-filters">Reset</Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Queue list */}
      <Card className="overflow-hidden">
        <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
          <div className="text-sm">
            <span className="font-semibold">{items.length}</span> items
            {filters.status ? ` · filtered to “${filters.status}”` : ""} · newest first
          </div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500">No items match these filters.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left text-xs">
              <tr>
                <th className="px-4 py-2">Published</th>
                <th className="px-4 py-2">Source</th>
                <th className="px-4 py-2">Title</th>
                <th className="px-4 py-2">Vendor / Product</th>
                <th className="px-4 py-2">CVE</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Severity</th>
                <th className="px-4 py-2">Assigned</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr
                  key={it.id}
                  onClick={() => setOpenId(it.id)}
                  className="border-t hover:bg-slate-50 cursor-pointer"
                  data-testid={`threat-row-${it.id}`}
                >
                  <td className="px-4 py-2 font-mono text-xs whitespace-nowrap">{fmtDate(it.date_published)}</td>
                  <td className="px-4 py-2">
                    <Badge className="bg-slate-100 text-slate-700 border-slate-200 uppercase text-[10px]">
                      {it.source}
                    </Badge>
                  </td>
                  <td className="px-4 py-2 max-w-md">
                    <div className="font-medium text-slate-800 truncate">{it.title}</div>
                    {it.ransomware_flag && (
                      <Badge className="mt-1 bg-rose-100 text-rose-800 border-rose-200 text-[10px]">
                        Ransomware campaign
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-600">
                    {it.vendor ? <div className="font-medium">{it.vendor}</div> : <span className="text-slate-400">—</span>}
                    {it.product ? <div className="text-slate-500">{it.product}</div> : null}
                  </td>
                  <td className="px-4 py-2 font-mono text-[11px] text-slate-600">
                    {(it.cve_ids || []).slice(0, 2).join(", ")}
                    {(it.cve_ids || []).length > 2 ? <span className="text-slate-400"> +{it.cve_ids.length - 2}</span> : null}
                  </td>
                  <td className="px-4 py-2">
                    <Badge className={STATUS_META[it.status]?.cls}>{it.status}</Badge>
                  </td>
                  <td className="px-4 py-2">
                    <Badge className={PRIORITY_META[it.priority]?.cls}>{it.priority}</Badge>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-600">
                    {it.assigned_to_name || <span className="text-slate-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <DetailDialog
        itemId={openId}
        onClose={() => setOpenId(null)}
        users={users}
        onChanged={async () => { await loadQueue(); }}
      />
    </div>
  );
}

/* -------------------------------------------------------------- */
/* Detail dialog                                                   */
/* -------------------------------------------------------------- */
function DetailDialog({ itemId, onClose, users, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [dismissOpen, setDismissOpen] = useState(false);
  const [dismissReason, setDismissReason] = useState("");
  const [enriching, setEnriching] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [workbenchOpen, setWorkbenchOpen] = useState(false);

  const load = async () => {
    if (!itemId) return;
    setLoading(true);
    try {
      const r = await api.get(`/threat-advisories/queue/${itemId}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load item");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (itemId) load(); /* eslint-disable-next-line */ }, [itemId]);

  if (!itemId) return null;
  const item = data?.item;

  const call = async (fn, successMsg) => {
    try {
      await fn();
      toast.success(successMsg);
      await Promise.all([load(), onChanged?.()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const setStatus = (status) => call(
    () => api.post(`/threat-advisories/queue/${itemId}/status`, { status }),
    `Status set to ${status}`,
  );
  const setPriority = (priority) => call(
    () => api.post(`/threat-advisories/queue/${itemId}/priority`, { priority }),
    `Severity set to ${priority}`,
  );
  const assign = (user_id) => call(
    () => api.post(`/threat-advisories/queue/${itemId}/assign`, { user_id: user_id || null }),
    user_id ? "Assigned" : "Unassigned",
  );
  const addNote = async () => {
    if (!noteText.trim()) return;
    await call(
      () => api.post(`/threat-advisories/queue/${itemId}/note`, { text: noteText.trim() }),
      "Note added",
    );
    setNoteText("");
  };
  const markActioned = (note) => call(
    () => api.post(`/threat-advisories/queue/${itemId}/action`, { note: note || null }),
    "Marked as Actioned",
  );
  const confirmDismiss = async () => {
    if (!dismissReason.trim()) { toast.error("Reason is required"); return; }
    await call(
      () => api.post(`/threat-advisories/queue/${itemId}/dismiss`, { reason: dismissReason.trim() }),
      "Dismissed",
    );
    setDismissReason("");
    setDismissOpen(false);
  };
  const enrich = async (force = false) => {
    setEnriching(true);
    try {
      const r = await api.post(`/threat-advisories/queue/${itemId}/enrich`, null, { params: force ? { force: true } : {} });
      toast.success(r.data?.cached ? "Loaded cached analysis" : "AI enrichment ready");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enrichment failed");
    } finally { setEnriching(false); }
  };
  const prepareAdvisory = async (force = false) => {
    setPreparing(true);
    try {
      const r = await api.post(`/threat-advisories/queue/${itemId}/advisory/prepare`, null, { params: force ? { force: true } : {} });
      toast.success(r.data?.cached ? "Loaded existing draft" : "Advisory draft ready — opening editor");
      await load();
      if (!r.data?.cached) setWorkbenchOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Prepare failed");
    } finally { setPreparing(false); }
  };

  return (
    <Dialog open={!!itemId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="threat-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-rose-600" />
            {item?.title || "Loading…"}
          </DialogTitle>
        </DialogHeader>

        {loading || !item ? (
          <div className="p-6 text-center text-slate-500">Loading…</div>
        ) : (
          <div className="space-y-5">
            {/* Top metadata */}
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-slate-100 text-slate-700 border-slate-200 uppercase">{item.source}</Badge>
              <Badge className={STATUS_META[item.status]?.cls}>{item.status}</Badge>
              <Badge className={PRIORITY_META[item.priority]?.cls}>Severity: {item.priority}</Badge>
              {item.ransomware_flag && (
                <Badge className="bg-rose-100 text-rose-800 border-rose-200">Ransomware campaign</Badge>
              )}
              {item.link && (
                <a href={item.link} target="_blank" rel="noreferrer"
                   className="ml-auto text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                   data-testid="threat-source-link">
                  Open original <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>

            {/* Field grid */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="Published">{fmtDate(item.date_published)}</Field>
              <Field label="Vendor">{item.vendor || "—"}</Field>
              <Field label="Product">{item.product || "—"}</Field>
              <Field label="Assigned to">{item.assigned_to_name || "—"}</Field>
              <Field label="CVE IDs" wide>
                {(item.cve_ids || []).length ? (
                  <div className="flex flex-wrap gap-1 items-center">
                    {item.cve_ids.map((c) => (
                      <a key={c} href={`https://nvd.nist.gov/vuln/detail/${c}`} target="_blank" rel="noreferrer"
                         className="font-mono text-[11px] px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 hover:bg-slate-100">
                        {c}
                      </a>
                    ))}
                    {item.link && (
                      <RescanCvesButton itemId={itemId} onDone={async () => { await load(); await onChanged?.(); }} />
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">—</span>
                    {item.link && (
                      <RescanCvesButton itemId={itemId} onDone={async () => { await load(); await onChanged?.(); }} />
                    )}
                  </div>
                )}
              </Field>
            </div>

            {/* Summary / description */}
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Summary</div>
              {item.source === "ncsc" && item.summary_html ? (
                <div
                  className="prose prose-sm max-w-none text-slate-700 border rounded p-3 bg-slate-50"
                  // NCSC HTML is stored verbatim per the user's choice. This is
                  // rendered to internal staff only (behind the CRM login) —
                  // no customer-facing surface here yet.
                  dangerouslySetInnerHTML={{ __html: item.summary_html }}
                />
              ) : (
                <div className="text-sm text-slate-700 whitespace-pre-wrap border rounded p-3 bg-slate-50">
                  {item.summary || "—"}
                </div>
              )}
            </div>

            {/* KEV extra block */}
            {item.source === "kev" && item.kev_extra && (
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">KEV catalogue details</div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Field label="Vulnerability name" wide>{item.kev_extra.vulnerabilityName || "—"}</Field>
                  <Field label="Date added">{fmtDate(item.kev_extra.dateAdded)}</Field>
                  <Field label="Due date">{fmtDate(item.kev_extra.dueDate)}</Field>
                  <Field label="Required action" wide>{item.kev_extra.requiredAction || "—"}</Field>
                  <Field label="Known ransomware use">{item.kev_extra.knownRansomwareCampaignUse || "—"}</Field>
                  <Field label="Notes" wide>{item.kev_extra.notes || "—"}</Field>
                </div>
              </div>
            )}

            {/* Quick actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t">
              <div>
                <label className="text-xs font-semibold text-slate-500 flex items-center gap-1 mb-1"><Flag className="h-3 w-3" /> Severity</label>
                <Select value={item.priority} onValueChange={setPriority}>
                  <SelectTrigger data-testid="threat-set-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 flex items-center gap-1 mb-1"><UserRound className="h-3 w-3" /> Assign to</label>
                <Select value={item.assigned_to_user_id || "none"} onValueChange={(v) => assign(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="threat-assign-user"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Unassigned —</SelectItem>
                    {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Status</label>
                <Select value={item.status === "Dismissed" ? "Dismissed" : item.status}
                        onValueChange={(v) => { if (v === "Dismissed") setDismissOpen(true); else setStatus(v); }}>
                  <SelectTrigger data-testid="threat-set-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.filter((s) => s !== "Actioned").map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => markActioned()}
                data-testid="threat-mark-actioned"
              >
                <CheckCircle2 className="h-4 w-4 mr-1" /> Mark Actioned
              </Button>
              <Button
                variant="outline"
                className="text-rose-700 border-rose-300 hover:bg-rose-50"
                onClick={() => setDismissOpen(true)}
                data-testid="threat-dismiss-open"
              >
                <XCircle className="h-4 w-4 mr-1" /> Dismiss…
              </Button>
              {!item.ai_enrichment && (
                <Button
                  onClick={() => enrich(false)}
                  disabled={enriching}
                  className="ml-auto bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white"
                  data-testid="threat-enrich-btn"
                >
                  <Sparkles className={`h-4 w-4 mr-1 ${enriching ? "animate-pulse" : ""}`} />
                  {enriching ? "Enriching with AI…" : "Enrich with AI"}
                </Button>
              )}
              {item.ai_enrichment && !item.advisory_draft && (
                <Button
                  onClick={() => prepareAdvisory(false)}
                  disabled={preparing}
                  className="ml-auto bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white"
                  data-testid="threat-prepare-advisory-btn"
                >
                  <FileText className={`h-4 w-4 mr-1 ${preparing ? "animate-pulse" : ""}`} />
                  {preparing ? "Preparing advisory…" : "Prepare Threat Advisory"}
                </Button>
              )}
            </div>

            {/* AI enrichment block */}
            {item.ai_enrichment && (
              <EnrichmentBlock
                enrichment={item.ai_enrichment}
                onRegenerate={() => enrich(true)}
                regenerating={enriching}
              />
            )}

            {/* Advisory draft + sends */}
            {item.advisory_draft && (
              <AdvisoryBlock
                itemId={itemId}
                draft={item.advisory_draft}
                sends={item.advisory_sends || []}
                onEdit={() => setWorkbenchOpen(true)}
                onRegenerate={() => prepareAdvisory(true)}
                regenerating={preparing}
              />
            )}

            {/* Notes */}
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">
                <StickyNote className="h-3 w-3" /> Internal notes
              </div>
              {(item.internal_notes || []).length === 0 ? (
                <div className="text-xs text-slate-400 italic">No notes yet.</div>
              ) : (
                <div className="space-y-2">
                  {item.internal_notes.map((n) => (
                    <div key={n.id} className="rounded border bg-slate-50 p-2 text-sm">
                      <div className="text-xs text-slate-500 mb-0.5">
                        <b>{n.author_name}</b> · {fmtDateTime(n.at)}
                      </div>
                      <div className="whitespace-pre-wrap">{n.text}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-2 flex gap-2">
                <Textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Add a note…"
                  rows={2}
                  data-testid="threat-note-input"
                />
                <Button onClick={addNote} disabled={!noteText.trim()} data-testid="threat-note-submit">
                  Add note
                </Button>
              </div>
            </div>

            {/* Dismissal reason, if any */}
            {item.status === "Dismissed" && item.dismissal_reason && (
              <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm">
                <div className="text-xs font-semibold text-slate-500 uppercase mb-1">Dismissal reason</div>
                <div>{item.dismissal_reason}</div>
              </div>
            )}

            {/* Audit log */}
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">
                <Clock className="h-3 w-3" /> Activity
              </div>
              {(data.audit || []).length === 0 ? (
                <div className="text-xs text-slate-400 italic">No activity recorded yet.</div>
              ) : (
                <ul className="space-y-1.5 text-xs">
                  {data.audit.map((a) => (
                    <li key={a.id} className="flex gap-2">
                      <span className="font-mono text-slate-400 whitespace-nowrap">{fmtDateTime(a.at)}</span>
                      <span className="text-slate-700">
                        <b>{a.actor_name}</b> · <AuditDescription a={a} />
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="threat-detail-close">Close</Button>
        </DialogFooter>
      </DialogContent>

      {/* Nested dismiss dialog */}
      <Dialog open={dismissOpen} onOpenChange={setDismissOpen}>
        <DialogContent data-testid="threat-dismiss-dialog">
          <DialogHeader><DialogTitle>Dismiss advisory</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">A reason is required for audit.</p>
            <Textarea
              value={dismissReason}
              onChange={(e) => setDismissReason(e.target.value)}
              placeholder="e.g. not relevant to our customer base"
              rows={3}
              data-testid="threat-dismiss-reason"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDismissOpen(false)}>Cancel</Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              disabled={!dismissReason.trim()}
              onClick={confirmDismiss}
              data-testid="threat-dismiss-confirm"
            >
              Dismiss
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Advisory workbench — fills the whole screen while editing */}
      {workbenchOpen && data?.item?.advisory_draft && (
        <AdvisoryWorkbench
          itemId={itemId}
          item={data.item}
          onClose={() => setWorkbenchOpen(false)}
          onSaved={async () => { await load(); await onChanged?.(); }}
        />
      )}
    </Dialog>
  );
}

function Field({ label, children, wide }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-0.5">{label}</div>
      <div className="text-slate-800">{children}</div>
    </div>
  );
}

function RescanCvesButton({ itemId, onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/threat-advisories/queue/${itemId}/rescan-cves`);
      const added = r.data?.added || [];
      if (added.length === 0) {
        toast.info("No new CVE IDs found in the article");
      } else {
        toast.success(`Found ${added.length} CVE${added.length === 1 ? "" : "s"} — re-run Enrich with AI to pick up the NVD severity`);
      }
      await onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rescan failed");
    } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      onClick={run}
      disabled={busy}
      className="text-[10px] px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 inline-flex items-center gap-1"
      data-testid="threat-rescan-cves-btn"
      title="Scrape the source article for any CVE IDs that weren't in the RSS summary"
    >
      <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} />
      {busy ? "Rescanning…" : "Rescan article for CVEs"}
    </button>
  );
}

function AuditDescription({ a }) {
  if (a.action === "assign") {
    const toName = a.after?.assigned_to_name;
    return toName ? <>assigned to <b>{toName}</b></> : <>unassigned</>;
  }
  if (a.action === "priority")      return <>set severity <b>{a.before || "Unset"}</b> → <b>{a.after}</b></>;
  if (a.action === "severity_sync") return <>synced severity to <b>{a.after}</b> from {a.extra?.from === "nvd" ? "NVD" : "AI"} enrichment</>;
  if (a.action === "status")   return <>set status <b>{a.before}</b> → <b>{a.after}</b></>;
  if (a.action === "note")     return <>added a note</>;
  if (a.action === "dismiss")  return <>dismissed — <i>{a.after?.dismissal_reason}</i></>;
  if (a.action === "action")   return <>marked as <b>Actioned</b></>;
  if (a.action === "enrich")   return <>ran AI enrichment{a.after?.severity ? <> — assessed <b>{a.after.severity}</b></> : null}</>;
  if (a.action === "advisory_prepare") return <>prepared customer advisory{a.after?.severity ? <> ({a.after.severity})</> : null}</>;
  if (a.action === "advisory_edit")    return <>edited advisory draft</>;
  if (a.action === "advisory_send")    return <>sent advisory to <b>{a.after?.success || 0}</b> recipient{(a.after?.success || 0) === 1 ? "" : "s"}{a.after?.failed > 0 ? <>, <b>{a.after.failed}</b> failed</> : null}</>;
  if (a.action === "rescan_cves") {
    const added = (a.extra?.added || []).length;
    return added ? <>rescanned the article and found <b>{added}</b> new CVE{added === 1 ? "" : "s"}</> : <>rescanned the article (no new CVEs)</>;
  }
  return <>{a.action}</>;
}

/* -------------------------------------------------------------- */
/* AI enrichment panel                                             */
/* -------------------------------------------------------------- */
const AI_SEV_CLS = {
  "Critical":      "bg-rose-100   text-rose-800   border-rose-200",
  "High":          "bg-orange-100 text-orange-800 border-orange-200",
  "Medium":        "bg-amber-100  text-amber-800  border-amber-200",
  "Low":           "bg-slate-100  text-slate-700  border-slate-200",
  "Informational": "bg-slate-50   text-slate-600  border-slate-200",
};

function EnrichmentBlock({ enrichment, onRegenerate, regenerating }) {
  const d = enrichment?.data || {};
  const share = d.share_with_customers === true;
  const nvd = enrichment?.nvd_severity;
  const nvdApplied = nvd?.override_applied;
  return (
    <div className="rounded-lg border border-violet-200 bg-gradient-to-br from-violet-50/60 to-indigo-50/40 p-4 space-y-4"
         data-testid="threat-ai-enrichment">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Sparkles className="h-4 w-4 text-violet-700" />
          <div className="font-semibold text-violet-900">AI analyst briefing</div>
          <Badge className={AI_SEV_CLS[d.severity_assessment] || AI_SEV_CLS.Low}>
            {d.severity_assessment || "Unknown"}
          </Badge>
          {nvdApplied && (
            <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 font-mono text-[10px]"
                   title={`Published CVSS score from NVD · ${nvd.primary_cve}`}
                   data-testid="threat-nvd-badge">
              NVD · {nvd.primary_cvss_version || "CVSS"} {Number(nvd.primary_score || 0).toFixed(1)}
            </Badge>
          )}
          <Badge className={share
            ? "bg-emerald-100 text-emerald-800 border-emerald-200"
            : "bg-slate-100 text-slate-600 border-slate-200"}>
            <Share2 className="h-3 w-3 mr-1" />
            {share ? "Recommend sharing" : "Hold internally"}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500 font-mono">
            {enrichment.model} · {enrichment.input_tokens || 0}/{enrichment.output_tokens || 0} tokens
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={onRegenerate}
            disabled={regenerating}
            data-testid="threat-enrich-regenerate"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${regenerating ? "animate-spin" : ""}`} />
            {regenerating ? "Regenerating…" : "Regenerate"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <AIField label="What it is">{d.what_it_is}</AIField>
        <AIField label="Severity rationale">{d.severity_rationale}</AIField>
        <AIField label="Exploitation">
          <div><b>{d.exploitation_status || "Unknown"}</b></div>
          <div className="text-slate-600 mt-0.5">{d.exploitation_notes}</div>
        </AIField>
        <AIField label="Attack vector / exposure">
          <div><b>{d.attack_vector || "Unknown"}</b></div>
          <div className="text-slate-600 mt-0.5">{d.typical_customer_exposure}</div>
        </AIField>
        <AIField label="Customer impact" wide>{d.customer_impact}</AIField>
      </div>

      {Array.isArray(d.affected_technologies) && d.affected_technologies.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Affected technologies</div>
          <div className="flex flex-wrap gap-1">
            {d.affected_technologies.map((t, i) => (
              <Badge key={i} className="bg-slate-100 text-slate-700 border-slate-200 font-normal">{t}</Badge>
            ))}
          </div>
        </div>
      )}

      {Array.isArray(d.immediate_actions) && d.immediate_actions.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Immediate actions for customers</div>
          <ol className="list-decimal list-inside space-y-1 text-sm text-slate-700">
            {d.immediate_actions.map((a, i) => <li key={i}>{a}</li>)}
          </ol>
        </div>
      )}

      {Array.isArray(d.detection_opportunities) && d.detection_opportunities.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1 flex items-center gap-1">
            <Shield className="h-3 w-3" /> SOC detection opportunities
          </div>
          <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
            {d.detection_opportunities.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </div>
      )}

      <div className={`rounded-md border p-3 text-sm ${
        share ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-slate-50"
      }`}>
        <div className="font-semibold mb-1 flex items-center gap-1">
          <Share2 className="h-4 w-4" />
          {share ? "Recommend sending to customers" : "Hold — do not send to customers"}
        </div>
        <div className="text-slate-700">{d.share_rationale}</div>
        {d.suggested_customer_scope && (
          <div className="text-xs text-slate-500 mt-1">Suggested scope: <b>{d.suggested_customer_scope}</b></div>
        )}
      </div>

      {Array.isArray(d.references) && d.references.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">References</div>
          <ul className="space-y-0.5 text-sm">
            {d.references.map((u, i) => (
              <li key={i}>
                <a href={u} target="_blank" rel="noreferrer"
                   className="text-blue-600 hover:underline inline-flex items-center gap-1 break-all">
                  {u} <ExternalLink className="h-3 w-3 flex-shrink-0" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="text-[11px] text-slate-400 italic border-t pt-2">
        Generated {fmtDateTime(enrichment.generated_at)} by {enrichment.generated_by_name}. AI output —
        always verify against the original source before sending to customers.
      </div>
    </div>
  );
}

function AIField({ label, children, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-0.5">{label}</div>
      <div className="text-slate-800">{children || <span className="text-slate-400">—</span>}</div>
    </div>
  );
}


/* -------------------------------------------------------------- */
/* Advisory block — compact summary + sends log inside DetailDialog */
/* -------------------------------------------------------------- */
function AdvisoryBlock({ itemId, draft, sends, onEdit, onRegenerate, regenerating }) {
  const d = draft?.data || {};
  const downloadPdf = async () => {
    try {
      const r = await api.get(`/threat-advisories/queue/${itemId}/advisory/download`, {
        params: { format: "pdf" }, responseType: "blob",
      });
      const fallback = (r.headers?.["x-advisory-fallback"] || "").toLowerCase();
      const ext = fallback === "docx" ? "docx" : "pdf";
      const mime = fallback === "docx"
        ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        : "application/pdf";
      _downloadBlob(r.data, `advisory.${ext}`, mime);
      if (fallback === "docx") {
        toast.info("PDF conversion isn't available on this environment — downloaded the DOCX instead.");
      }
    } catch (e) {
      let detail = "Download failed";
      try {
        if (e?.response?.data instanceof Blob) {
          const text = await e.response.data.text();
          const j = JSON.parse(text);
          if (j?.detail) detail = j.detail;
        } else if (e?.response?.data?.detail) {
          detail = e.response.data.detail;
        }
      } catch (_) { /* leave generic message */ }
      toast.error(detail);
    }
  };
  const downloadSent = async (send) => {
    try {
      const r = await api.get(`/threat-advisories/queue/${itemId}/advisory/sends/${send.id}/download`, { responseType: "blob" });
      _downloadBlob(r.data, send.filename || "advisory.pdf", "application/pdf");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fetch stored send");
    }
  };
  return (
    <div className="rounded-lg border border-rose-200 bg-gradient-to-br from-rose-50/60 to-pink-50/40 p-4 space-y-3"
         data-testid="threat-advisory-block">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-rose-700" />
          <div className="font-semibold text-rose-900">Customer-facing advisory</div>
          <Badge className={AI_SEV_CLS[d.severity] || AI_SEV_CLS.Low}>{d.severity || "—"}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={onEdit} data-testid="threat-advisory-edit-btn">
            <Pencil className="h-3.5 w-3.5 mr-1" /> Preview &amp; edit
          </Button>
          <Button size="sm" variant="outline" onClick={downloadPdf} data-testid="threat-advisory-download-btn">
            <Download className="h-3.5 w-3.5 mr-1" /> Download PDF
          </Button>
          <Button size="sm" variant="outline" onClick={onRegenerate} disabled={regenerating}
                  data-testid="threat-advisory-regenerate-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${regenerating ? "animate-spin" : ""}`} /> Regenerate
          </Button>
        </div>
      </div>
      <div className="text-sm">
        <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide mb-0.5">{d.advisory_title || "Untitled advisory"}</div>
        <div className="text-slate-700 line-clamp-3">{d.executive_summary || "—"}</div>
      </div>
      <div className="text-[11px] text-slate-500 flex items-center justify-between gap-2 flex-wrap pt-2 border-t">
        <div>
          Draft generated {fmtDateTime(draft.generated_at)} by {draft.generated_by_name}
          {draft.edited_at && <> · last edited {fmtDateTime(draft.edited_at)} by {draft.edited_by_name}</>}
        </div>
        <div className="font-mono">{draft.model}</div>
      </div>
      {sends.length > 0 && (
        <div className="pt-2 border-t">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1 flex items-center gap-1">
            <Mail className="h-3 w-3" /> Send log ({sends.length})
          </div>
          <ul className="space-y-1 text-xs">
            {sends.slice().reverse().map((s) => (
              <li key={s.id} className="flex items-center gap-2 rounded border bg-white p-2">
                <div className="flex-1">
                  <div>
                    <b>{fmtDateTime(s.sent_at)}</b> · {s.sent_by_name} · {s.success_count} delivered
                    {s.failed_count > 0 && <span className="text-rose-700"> · {s.failed_count} failed</span>}
                  </div>
                  <div className="text-slate-500 font-mono text-[10px] truncate">
                    {s.recipients.map((r) => r.email).slice(0, 6).join(", ")}
                    {s.recipients.length > 6 ? ` +${s.recipients.length - 6}` : ""}
                  </div>
                </div>
                {s.stored_path && (
                  <Button size="sm" variant="ghost" onClick={() => downloadSent(s)}
                          data-testid={`threat-advisory-sent-download-${s.id}`}>
                    <Download className="h-3 w-3" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function _downloadBlob(data, name, mime) {
  const blob = new Blob([data], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

/* -------------------------------------------------------------- */
/* Advisory Workbench — large editor with per-field textareas + send */
/* -------------------------------------------------------------- */
const EDITABLE_FIELDS = [
  { key: "advisory_title",         label: "Advisory title",      lines: 1 },
  { key: "advisory_date",          label: "Advisory date",       lines: 1 },
  { key: "severity",               label: "Severity",            lines: 1 },
  { key: "executive_summary",      label: "Executive summary",   lines: 4 },
  { key: "what_it_is",             label: "What it is",          lines: 4 },
  { key: "affected_technologies",  label: "Affected technologies (one per line)", lines: 4, list: true },
  { key: "exploitation_status",    label: "Exploitation status", lines: 1 },
  { key: "attack_vector",          label: "Attack vector",       lines: 1 },
  { key: "customer_impact",        label: "Customer impact",     lines: 4 },
  { key: "immediate_actions",      label: "Immediate actions (one per line)",      lines: 5, list: true },
  { key: "detection_opportunities",label: "Detection opportunities (one per line)",lines: 3, list: true },
  { key: "cve_ids",                label: "CVE IDs (one per line)",                lines: 2, list: true },
  { key: "source_name",            label: "Source name",         lines: 1 },
  { key: "source_link",            label: "Source link",         lines: 1 },
  { key: "references",             label: "References (one URL per line)",         lines: 3, list: true },
];

function AdvisoryWorkbench({ itemId, item, onClose, onSaved }) {
  const draft = item.advisory_draft;
  const [form, setForm] = useState(() => {
    const d = { ...(draft?.data || {}) };
    EDITABLE_FIELDS.forEach((f) => {
      if (f.list && Array.isArray(d[f.key])) d[f.key] = d[f.key].join("\n");
      if (d[f.key] === null || d[f.key] === undefined) d[f.key] = "";
    });
    return d;
  });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);

  const update = (k, v) => { setForm((p) => ({ ...p, [k]: v })); setDirty(true); };

  const serialise = () => {
    const out = {};
    EDITABLE_FIELDS.forEach((f) => {
      if (f.list) {
        out[f.key] = String(form[f.key] || "").split("\n").map((s) => s.trim()).filter(Boolean);
      } else {
        out[f.key] = String(form[f.key] || "").trim();
      }
    });
    return out;
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/threat-advisories/queue/${itemId}/advisory/draft`, { data: serialise() });
      toast.success("Draft saved");
      setDirty(false);
      await onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const downloadFormat = async (format) => {
    if (dirty) {
      // Auto-save first so the file reflects the current edits.
      await save();
    }
    try {
      const r = await api.get(`/threat-advisories/queue/${itemId}/advisory/download`, {
        params: { format }, responseType: "blob",
      });
      // If the backend couldn't produce the requested PDF (LibreOffice
      // missing on the deployment image) it falls back to DOCX. Respect the
      // real content-type so the file saves with the right extension.
      const fallback = (r.headers?.["x-advisory-fallback"] || "").toLowerCase();
      const realFormat = fallback === "docx" ? "docx" : format;
      const ext = realFormat === "docx" ? "docx" : "pdf";
      const mime = realFormat === "docx"
        ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        : "application/pdf";
      _downloadBlob(r.data, `advisory.${ext}`, mime);
      if (fallback === "docx" && format === "pdf") {
        toast.info("PDF conversion isn't available on this environment — downloaded the DOCX instead.");
      }
    } catch (e) {
      // Blob responses hide the JSON error body — parse it so the engineer
      // sees the real reason instead of a generic 'Download failed'.
      let detail = "Download failed";
      try {
        if (e?.response?.data instanceof Blob) {
          const text = await e.response.data.text();
          const j = JSON.parse(text);
          if (j?.detail) detail = j.detail;
        } else if (e?.response?.data?.detail) {
          detail = e.response.data.detail;
        }
      } catch (_) { /* best-effort — leave generic message */ }
      toast.error(detail);
    }
  };

  const openSend = async () => {
    if (dirty) { await save(); }
    setSendOpen(true);
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[94vh] overflow-y-auto"
                     data-testid="threat-advisory-workbench">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-rose-600" />
            Advisory workbench
            {dirty && <Badge className="bg-amber-100 text-amber-800 border-amber-200">Unsaved</Badge>}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          {EDITABLE_FIELDS.map((f) => {
            const val = form[f.key] || "";
            // The template's heading box is single-line; warn when the title
            // is likely to wrap and overlap the subtitle above it.
            const titleWarn = f.key === "advisory_title" && val.length > 55;
            return (
              <div key={f.key} className={f.lines > 1 ? "md:col-span-2" : ""}>
                <label className="text-xs font-semibold text-slate-600 mb-1 flex items-center justify-between">
                  <span>{f.label}</span>
                  {f.key === "advisory_title" && (
                    <span className={`text-[10px] font-mono ${titleWarn ? "text-amber-700" : "text-slate-400"}`}>
                      {val.length}/55 {titleWarn && "· may wrap"}
                    </span>
                  )}
                </label>
                {f.lines > 1 ? (
                  <Textarea
                    value={val}
                    onChange={(e) => update(f.key, e.target.value)}
                    rows={f.lines}
                    data-testid={`threat-advisory-field-${f.key}`}
                  />
                ) : (
                  <Input
                    value={val}
                    onChange={(e) => update(f.key, e.target.value)}
                    className={titleWarn ? "border-amber-300 focus:border-amber-500" : ""}
                    data-testid={`threat-advisory-field-${f.key}`}
                  />
                )}
                {titleWarn && (
                  <div className="text-[11px] text-amber-700 mt-0.5">
                    Over 55 chars — the PDF will auto-shrink the title font to fit, but shortening it looks cleaner.
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <DialogFooter className="mt-4 flex-wrap gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={save} disabled={saving || !dirty}
                  data-testid="threat-advisory-save-btn">
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</> : "Save draft"}
          </Button>
          <Button variant="outline" onClick={() => downloadFormat("docx")}
                  data-testid="threat-advisory-download-docx-btn">
            <Download className="h-4 w-4 mr-1" /> Download DOCX
          </Button>
          <Button variant="outline" onClick={() => downloadFormat("pdf")}
                  data-testid="threat-advisory-download-pdf-btn">
            <Download className="h-4 w-4 mr-1" /> Download PDF
          </Button>
          <Button
            onClick={openSend}
            className="bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white"
            data-testid="threat-advisory-send-btn"
          >
            <Send className="h-4 w-4 mr-1" /> Send to customers…
          </Button>
        </DialogFooter>

        {sendOpen && (
          <ContactPickerDialog
            itemId={itemId}
            title={form.advisory_title || item.title}
            onClose={() => setSendOpen(false)}
            onSent={async () => { setSendOpen(false); await onSaved?.(); onClose(); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------- */
/* Contact picker dialog + send                                    */
/* -------------------------------------------------------------- */
function ContactPickerDialog({ itemId, title, onClose, onSent }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState({}); // id -> contact
  const [extraEmails, setExtraEmails] = useState("");
  const [subject, setSubject] = useState(`Xentra Threat Advisory: ${title || "Alert"}`);
  const [intro, setIntro] = useState(
    "<p>Dear {{first_name}},</p>" +
    "<p>Please find attached the latest Xentra Threat Advisory, which may be relevant to your organisation. " +
    "We'd recommend reviewing the Immediate Actions section and getting in touch if you'd like our help investigating or remediating.</p>" +
    "<p>Kind regards,<br/>Xentra Support Team<br/><a href=\"mailto:support@xentra.co.uk\">support@xentra.co.uk</a></p>"
  );
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [optedInCount, setOptedInCount] = useState(null); // null = still loading, number = done

  const search = async (query) => {
    setLoading(true);
    try {
      const r = await api.get("/threat-advisories/contacts/search", { params: { q: query || "", limit: 50 } });
      setResults(r.data?.contacts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally { setLoading(false); }
  };
  // Pre-populate the picked list with every contact flagged
  // "Receive Threat Advisories" on their contact card. Engineer can still
  // un-tick before sending; the search list below shows the full roster for
  // ad-hoc additions.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/threat-advisories/contacts/opted-in");
        const list = r.data?.contacts || [];
        const map = {};
        for (const c of list) map[c.id] = c;
        setPicked(map);
        setOptedInCount(list.length);
      } catch {
        setOptedInCount(0);
      }
    })();
    search("");
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(() => search(q), 250);
    return () => clearTimeout(t);
  }, [q]);  // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (c) => setPicked((p) => {
    const next = { ...p };
    if (next[c.id]) delete next[c.id];
    else next[c.id] = c;
    return next;
  });

  const pickedList = Object.values(picked);
  const extras = extraEmails.split(/[\s,;]+/).map((s) => s.trim()).filter((s) => s && s.includes("@"));
  const total = pickedList.length + extras.length;

  const send = async () => {
    if (total === 0) { toast.error("Select at least one recipient"); return; }
    if (!window.confirm(`Send this advisory to ${total} recipient${total === 1 ? "" : "s"} from support@xentra.co.uk?`)) return;
    setSending(true);
    try {
      const r = await api.post(`/threat-advisories/queue/${itemId}/advisory/send`, {
        recipient_contact_ids: pickedList.map((c) => c.id),
        recipient_emails: extras,
        subject,
        intro_html: intro,
      });
      const s = r.data?.send || {};
      const firstErr = (s.recipients || []).find((r) => r.error)?.error;
      if (s.failed_count > 0) {
        toast.warning(
          `Sent to ${s.success_count}/${s.success_count + s.failed_count} — ${s.failed_count} failed`
          + (firstErr ? ` · ${String(firstErr).slice(0, 160)}` : ""),
          { duration: 10000 },
        );
      } else {
        toast.success(`Advisory sent to ${s.success_count} recipient${s.success_count === 1 ? "" : "s"}`);
      }
      await onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[94vh] overflow-y-auto"
                     data-testid="threat-advisory-send-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-rose-600" /> Send advisory to customers
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                   data-testid="threat-advisory-send-subject" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">
              Email intro (HTML allowed · <code className="bg-slate-100 px-1">{"{{first_name}}"}</code> will be personalised)
            </label>
            <Textarea rows={5} value={intro} onChange={(e) => setIntro(e.target.value)}
                      data-testid="threat-advisory-send-intro" />
          </div>
          <div className="border-t pt-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                Pick customer contacts ({pickedList.length} selected)
              </label>
              {pickedList.length > 0 && (
                <Button size="sm" variant="ghost" onClick={() => setPicked({})}
                        className="text-rose-700 hover:bg-rose-50">
                  <Trash2 className="h-3 w-3 mr-1" /> Clear
                </Button>
              )}
            </div>
            {optedInCount !== null && (
              <div className="mb-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5" data-testid="threat-advisory-optedin-hint">
                {optedInCount > 0
                  ? <>Pre-selected <b>{optedInCount}</b> contact{optedInCount === 1 ? "" : "s"} flagged <b>“Receive Threat Advisories”</b> on their contact card. Un-tick any you don't want on this send.</>
                  : <>No contacts are currently flagged <b>“Receive Threat Advisories”</b>. Tick the box on a contact's card to auto-add them to future advisory sends.</>}
              </div>
            )}
            <div className="relative mb-2">
              <Search className="h-4 w-4 absolute left-2 top-2.5 text-slate-400 pointer-events-none" />
              <Input placeholder="Search by name, email, account, job title…" className="pl-8"
                     value={q} onChange={(e) => setQ(e.target.value)}
                     data-testid="threat-advisory-contact-search" />
            </div>
            <div className="max-h-72 overflow-y-auto border rounded">
              {loading ? (
                <div className="p-6 text-center text-slate-500 text-sm">Searching…</div>
              ) : results.length === 0 ? (
                <div className="p-6 text-center text-slate-500 text-sm">
                  No contacts match. Try a different search or add emails manually below.
                </div>
              ) : (
                <ul>
                  {results.map((c) => {
                    const name = c.full_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email;
                    const sel = !!picked[c.id];
                    return (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => toggle(c)}
                          className={`w-full text-left px-3 py-2 text-sm border-b last:border-b-0 flex items-start gap-3 hover:bg-slate-50 ${sel ? "bg-emerald-50" : ""}`}
                          data-testid={`threat-advisory-contact-${c.id}`}
                        >
                          <input type="checkbox" checked={sel} readOnly className="mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium">{name}</div>
                            <div className="text-xs text-slate-500 truncate">
                              {c.email}{c.job_title ? ` · ${c.job_title}` : ""}{c.account_name ? ` · ${c.account_name}` : ""}
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">
              Or paste extra email addresses (not in CRM — comma or newline separated)
            </label>
            <Textarea rows={2} value={extraEmails} onChange={(e) => setExtraEmails(e.target.value)}
                      placeholder="jane@customer.com, bob@customer.co.uk"
                      data-testid="threat-advisory-extra-emails" />
            {extras.length > 0 && (
              <div className="text-[11px] text-slate-500 mt-1">+ {extras.length} manual recipient{extras.length === 1 ? "" : "s"}</div>
            )}
          </div>
          <div className="rounded-md border border-amber-200 bg-amber-50/60 p-2 text-xs text-amber-800">
            <AlertTriangle className="h-3 w-3 inline mr-1" />
            Each recipient will receive their own personal email from <b>support@xentra.co.uk</b> with the advisory PDF
            attached. The alert will be marked as <b>Actioned</b> once the send completes.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={send}
            disabled={sending || total === 0}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="threat-advisory-send-confirm"
          >
            {sending ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Sending {total}…</> : <><Send className="h-4 w-4 mr-1" /> Send to {total} recipient{total === 1 ? "" : "s"}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

